import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../../App/redux/store';
import { APIStatus } from '../../services/axiosEnums';

import { isEmpty } from 'lodash';
import { initialPagination } from './constants';
import deleteAdsThunk from './services/thunks/deleteAdsThunk';
import fetchAdPlotLocationsThunk from './services/thunks/fetchAdPlotLocationsThunk';
import fetchAdsThunk from './services/thunks/fetchAdsThunk';
import fetchAdThunk from './services/thunks/fetchAdThunk';
import fetchAdDpeLocationsThunk from './services/thunks/fetchAdDpeLocationsThunk';

const initialState: AdState = {
  pagination: initialPagination,
  ads: { apiStatus: APIStatus.IDLE, result: null },
  activeAd: { apiStatus: APIStatus.IDLE, result: null },
  selectedAds: [],
  similarAdPlots: { apiStatus: APIStatus.IDLE, result: null },
  similarAdDpes: { apiStatus: APIStatus.IDLE, result: null },
  adGeoAddress: null,
  deleteErrorIds: [],
  highlightedPlotId: null,
  highlightedDpeId: null,
  highlightedDpeIdIsScrolling: false,
  adDetailsDpeFormValues: null,
  adDetailsAreaFormValues: null,
};

const adSlice = createSlice({
  name: 'ad',
  initialState,
  reducers: {
    reset: () => initialState,
    adsApiSet: (
      state,
      action: PayloadAction<Omit<IApiState<Ads | null>, 'result'>>,
    ) => {
      state.ads.apiStatus = action.payload.apiStatus;
      state.ads.error = action.payload.error;
    },
    adGeoAddressSet: (state, action: PayloadAction<string | null>) => {
      state.adGeoAddress = action.payload;
    },
    adGeoAddressToggle: (state, action: PayloadAction<string>) => {
      const address = action.payload;
      // If the address is already selected, deselect it
      if (state.adGeoAddress === address) {
        state.adGeoAddress = null;
      } else {
        state.adGeoAddress = address;
      }
    },
    highlightedPlotIdSet: (state, action: PayloadAction<string | null>) => {
      state.highlightedPlotId = action.payload;
    },
    highlightedDpeIdSet: (state, action: PayloadAction<string | null>) => {
      state.highlightedDpeId = action.payload;
    },
    highlightedDpeIdIsScrollingSet: (state, action: PayloadAction<boolean>) => {
      state.highlightedDpeIdIsScrolling = action.payload;
    },
    paginationSet: (state, action: PayloadAction<AdPaginationDatas>) => {
      state.pagination = action.payload;
    },
    activeAdReset: (state) => {
      state.activeAd = { apiStatus: APIStatus.IDLE, result: null, error: null };
      // reset ad plots too
      state.similarAdPlots = {
        apiStatus: APIStatus.IDLE,
        result: null,
        error: null,
      };
      // reset ad dpes too
      state.similarAdDpes = {
        apiStatus: APIStatus.IDLE,
        result: null,
        error: null,
      };
      state.highlightedPlotId = null;
      state.highlightedDpeId = null;
      // reset form values
      state.adDetailsDpeFormValues = null;
      state.adDetailsAreaFormValues = null;
    },
    geoAddressReset: (state) => {
      state.adGeoAddress = null;
    },
    selectedAdsAdd: (state, action: PayloadAction<Ad>) => {
      const ap = action.payload;
      if (state.selectedAds.map((m) => m.idIri).includes(ap.idIri)) {
        state.selectedAds = state.selectedAds.filter(
          (elt) => elt.idIri !== ap.idIri,
        );
      } else {
        state.selectedAds = state.selectedAds.concat(ap);
      }
    },
    selectedAdsReset: (state) => {
      state.selectedAds = [];
    },
    adDetailsDpeFormValuesSet: (state, action: PayloadAction<any>) => {
      state.adDetailsDpeFormValues = action.payload;
    },
    adDetailsAreaFormValuesSet: (state, action: PayloadAction<any>) => {
      state.adDetailsAreaFormValues = action.payload;
    },
  },
  extraReducers(builder) {
    // FETCH ADS
    builder
      .addCase(fetchAdsThunk.pending, (state) => {
        state.ads.apiStatus = APIStatus.PENDING;
        state.ads.error = undefined;
      })
      .addCase(
        fetchAdsThunk.fulfilled,
        (
          state,
          action: PayloadAction<{ ads: Ads; pagination: AdPaginationDatas }>,
        ) => {
          state.ads.apiStatus = APIStatus.IDLE;
          state.ads.result = action.payload.ads;
          state.pagination = action.payload.pagination;
          state.ads.error = undefined;
        },
      )
      .addCase(fetchAdsThunk.rejected, (state, action) => {
        state.ads.apiStatus = APIStatus.REJECTED;
        state.ads.error = action.error;
      });
    // FETCH ACTIVE AD
    builder
      .addCase(fetchAdThunk.pending, (state) => {
        state.activeAd.apiStatus = APIStatus.PENDING;
        state.activeAd.error = undefined;
      })
      .addCase(fetchAdThunk.fulfilled, (state, action: PayloadAction<Ad>) => {
        state.activeAd.apiStatus = APIStatus.IDLE;
        state.activeAd.result = action.payload;
        state.activeAd.error = undefined;
      })
      .addCase(fetchAdThunk.rejected, (state, action) => {
        state.activeAd.apiStatus = APIStatus.REJECTED;
        state.activeAd.error = action.error;
      });
    // FETCH ACTIVE AD PLOT LOCATION
    builder
      .addCase(fetchAdPlotLocationsThunk.pending, (state) => {
        state.similarAdPlots.apiStatus = APIStatus.PENDING;
        state.similarAdPlots.error = undefined;
      })
      .addCase(
        fetchAdPlotLocationsThunk.fulfilled,
        (state, action: PayloadAction<AdPlots>) => {
          state.similarAdPlots.apiStatus = APIStatus.IDLE;
          state.similarAdPlots.result = action.payload;
          state.similarAdPlots.error = undefined;
        },
      )
      .addCase(fetchAdPlotLocationsThunk.rejected, (state, action) => {
        state.similarAdPlots.apiStatus = APIStatus.REJECTED;
        state.similarAdPlots.error = action.error;
      });
    // FETCH AD DPE LOCATIONS
    builder
      .addCase(fetchAdDpeLocationsThunk.pending, (state) => {
        state.similarAdDpes.apiStatus = APIStatus.PENDING;
        state.similarAdDpes.error = undefined;
      })
      .addCase(
        fetchAdDpeLocationsThunk.fulfilled,
        (state, action: PayloadAction<AdDpes>) => {
          state.similarAdDpes.apiStatus = APIStatus.IDLE;
          state.similarAdDpes.result = action.payload;
          state.similarAdDpes.error = undefined;
        },
      )
      .addCase(fetchAdDpeLocationsThunk.rejected, (state, action) => {
        state.similarAdDpes.apiStatus = APIStatus.REJECTED;
        state.similarAdDpes.error = action.error;
      });

    // DELETE ADS
    builder
      .addCase(deleteAdsThunk.pending, (state) => {
        state.ads.apiStatus = APIStatus.PENDING;
        state.ads.error = undefined;
      })
      .addCase(deleteAdsThunk.fulfilled, (state) => {
        state.ads.apiStatus = APIStatus.IDLE;
        // if activeAd exists, reset it but no selectedAds
        if (state.activeAd.result) {
          state.activeAd.result = null;
        } else if (!isEmpty(state.selectedAds)) {
          state.selectedAds = [];
        }
        state.ads.error = undefined;
      })
      .addCase(deleteAdsThunk.rejected, (state, action) => {
        state.ads.apiStatus = APIStatus.REJECTED;
        state.ads.error = action.error;
      });
  },
});

export default adSlice.reducer;
export const adActions = adSlice.actions;
export const getAdState = (state: RootState) => state.ad;
